import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Layout from '../layout/Layout'
import InvoiceForm from '../molecules/InvoiceForm'
import { vehicleFieldsFromMotorbike } from '../molecules/SellContractForm'
import ChevronRightSvg from '../other/svg/ChevronRightSvg'
import { formatClientDetailsName } from 'src/helpers/formatClientDetailsName'
import { InvoiceFormData } from 'src/helpers/invoiceFormValidation'
import { CREATE_INVOICE, RESET_INVOICE_PDF } from 'src/redux/invoiceReducer'
import { FETCH_CLIENT } from 'src/redux/clientReducer'
import { RootReducer } from 'src/redux/rootReducer'
import { parseOptionalNumericId, useDocumentCreateParams } from 'src/helpers/addVehicleDocument'

const InvoiceCreate = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { clientId, vehicleId, returnPath } = useDocumentCreateParams()
  const loading = useSelector((state: RootReducer) => state.invoiceReducer.loading)
  const invoicePdf = useSelector((state: RootReducer) => state.invoiceReducer.invoice_pdf)
  const motorbike = useSelector((state: RootReducer) => {
    return state.motorbikesReducer.motorbikes.find((item) => item.id === vehicleId) ?? null
  })
  const client = useSelector((state: RootReducer) => {
    if (typeof clientId === 'string') {
      return state.clientReducer.client[clientId] ?? null
    }
    return null
  })

  useEffect(() => {
    if (typeof clientId === 'string') {
      dispatch(FETCH_CLIENT(clientId))
    }
  }, [dispatch, clientId])

  useEffect(() => {
    return () => {
      dispatch(RESET_INVOICE_PDF())
    }
  }, [dispatch])

  useEffect(() => {
    if (invoicePdf !== null) {
      window.open(invoicePdf, '_blank')
      navigate(returnPath)
    }
  }, [invoicePdf, navigate, returnPath])

  const handleSubmit = (values: InvoiceFormData): void => {
    const formClientId = parseOptionalNumericId(values.client_id)
    const resolvedClientId = formClientId === 0 ? parseOptionalNumericId(clientId) : formClientId
    if (resolvedClientId === 0) {
      return
    }
    dispatch(
      CREATE_INVOICE({
        ...values,
        client_id: resolvedClientId
      })
    )
  }

  const handleCancel = (): void => {
    navigate(returnPath)
  }

  const vehicleFields = motorbike === null ? null : vehicleFieldsFromMotorbike(motorbike)

  return (
    <Layout withoutToolbar>
      <div className="flex flex-col justify-start items-start gap-4 h-[calc(100vh-100px)]">
        <div className="self-stretch min-h-14 bg-Estigroup-primary rounded-lg border border-neutral-200 flex flex-col justify-start items-center">
          <div className="self-stretch px-4 py-3 inline-flex justify-start items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat leading-5">
              {t('invoice_form.sales_invoices')}
            </button>
            <ChevronRightSvg className="size-3.5 text-foregrounds-fg-on-color" />
            <div className="text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat leading-5">
              {t('invoice_form.add_invoice')}
            </div>
          </div>
        </div>
        <InvoiceForm
          key={`${clientId ?? ''}-${client === null ? 'pending' : String(client.id)}-${vehicleId ?? ''}`}
          initialValues={{
            language: 'pl',
            additional_text: '',
            currency_code: 'PLN',
            price_type: 'gross',
            price: '',
            vat_option: '',
            payment_method: '',
            payment_due_date: '',
            invoice_number: '',
            invoice_date: '',
            delivery_date: '',
            client_id: clientId,
            ...(client === null
              ? {}
              : {
                  client_name: formatClientDetailsName(client),
                  client_tax_number: client.tax_number ?? '',
                  client_street: client.street,
                  client_house_number: client.house_number,
                  client_zip_code: client.zip_code,
                  client_place: client.place
                }),
            ...(vehicleFields === null
              ? {
                  motorbike_id: vehicleId,
                  vin: ''
                }
              : {
                  motorbike_id: vehicleFields.motorbike_id,
                  motorbike_name: vehicleFields.motorbike_name,
                  vin: vehicleFields.vin,
                  motorbike_mileage: vehicleFields.motorbike_mileage,
                  motorbike_year: vehicleFields.motorbike_year
                })
          }}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          mode="create"
          loading={loading}
        />
      </div>
    </Layout>
  )
}

export default InvoiceCreate
